import type { ReactNode } from 'react'
import type { ConversationSummary } from '@shared/services'
import { TileFrame } from './TileFrame'

interface ProjectTileProps {
  project: ConversationSummary
  glow: string
  pinned: boolean
  onTogglePin: () => void
  onDelete: () => void
  onShare: () => void
  /** When it was last spoken in, already put into words. */
  when: string
  /** A colour the person chose (2026-10-21): the edge shows it all the time. */
  coloured?: boolean
  /** A picture of the person's own behind the card, faint (2026-10-21). */
  picture?: string
  /** The card's own buttons, drawn inside it so they move with it. */
  children?: ReactNode
}

/**
 * One conversation in the overview's project grid.
 *
 * The same shell as a skill tile, on purpose: the two grids sit under one tab
 * strip on one page, and a card that lights up differently from the card
 * beside it reads as a different piece of software.
 */
export function ProjectTile({
  project,
  glow,
  pinned,
  onTogglePin,
  onDelete,
  onShare,
  when,
  coloured = false,
  picture,
  children,
}: ProjectTileProps) {
  // Shared with this person and not opened yet: a dot, until it is.
  const unseen = Boolean(project.shared_by) && !project.seen_at
  const sharedOn = project.shared_at
    ? new Date(project.shared_at).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })
    : ''
  return (
    <TileFrame
      glow={glow}
      to={`/projects/${project.id}`}
      newTab
      label={project.title}
      pinned={pinned}
      onTogglePin={onTogglePin}
      onDelete={onDelete}
      onShare={onShare}
      className={`${coloured ? 'ww-tile--coloured' : ''}${picture ? ' ww-tile--picture' : ''}`.trim()}
    >
      {picture ? <span className="ww-tile__picture" aria-hidden="true" style={{ backgroundImage: `url("${picture}")` }} /> : null}
      {unseen ? <span className="ww-tile__new" title="New — shared with you" /> : null}
      <span className="ww-tile__name">{project.title}</span>
      <span className="ww-tile__summary">{when}</span>
      {project.shared_by ? (
        <span className="ww-tile__shared">
          Shared by {project.shared_by}
          {sharedOn ? ` · ${sharedOn}` : ''}
        </span>
      ) : null}
      {children}
    </TileFrame>
  )
}
